/* Copyright (c) 2023-2025 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useMemo, useState } from "react";
import { Unless } from "react-if";
import { Resource, ResourcesSortFields, SortOrder } from "~/generated/graphql";
import { NoData } from "~/components/NoData";
import { Pagination } from "~/components/Pagination/Pagination";
import { duplicateIconConfig } from "~/components/resourceGroup/constants";
import {
  getNoSearchResultsMessage,
  minSearchLength,
} from "~/components/SearchInput";
import { SidebarSelector } from "~/components/SidebarWithMultiSelect/SidebarSelector";
import { useSearchState } from "~/hooks/useSearchState";
import { isNotNil } from "~/tools";
import { PlanField } from "~/views/plans/PlanCreate/components/PlanCreateWizard/constants";
import { usePlanWizard } from "~/views/plans/PlanCreate/components/PlanCreateWizard/PlanProvider";
import { CreateResourceGroupModal } from "~/views/plans/PlanCreate/components/PlanCreateWizard/PlanSettingsForms/components/AddResources/CreateResourceGroupModal";
import {
  AddResourceSidebarState,
  SidebarType,
} from "~/views/plans/PlanCreate/components/PlanCreateWizard/PlanSettingsForms/components/AddResources/types";
import {
  formatSidebarOptions,
  getSelectedOptions,
} from "~/views/plans/PlanCreate/components/PlanCreateWizard/PlanSettingsForms/components/AddResources/utils";
import { updateEntities } from "~/views/plans/PlanCreate/components/PlanCreateWizard/PlanSettingsForms/components/utils";
import { useStepState } from "~/views/plans/PlanCreate/components/PlanCreateWizard/PlanSettingsForms/StepProvider";
import { CreateResourceGroup } from "~/views/plans/PlanCreate/components/PlanCreateWizard/types";
import { useResourcesSearch } from "~/views/resources/hooks/useResourcesSearch/useResourcesSearch";

type Props = {
  refetch: () => void;
  sidebarState: AddResourceSidebarState;
  updateSidebarState: (state: AddResourceSidebarState) => void;
  alreadyAddedResourceIds?: Set<Resource["id"]>;
};

export function AddResourcesSidebar({
  refetch,
  sidebarState,
  updateSidebarState,
  alreadyAddedResourceIds,
}: Props) {
  const { planData, setPlanData } = usePlanWizard();
  const { stepState, updateStepState } = useStepState();

  const { searchState, updateSearchState } = useSearchState({
    page: 1,
    limit: 20,
    sort: { field: ResourcesSortFields.Name, order: SortOrder.Asc },
    filter: { phrase: "" },
  });

  const phrase = searchState.filter?.phrase ?? "";
  const trimmedPhrase = phrase.trim();
  const meetsMin = trimmedPhrase.length >= minSearchLength;
  const phraseToSend = meetsMin ? trimmedPhrase : undefined;
  const isSearchAllowed = trimmedPhrase.length === 0 || meetsMin;

  const { data, loading, count } = useResourcesSearch({
    page: String(searchState.page),
    limit: String(searchState.limit),
    sort: {
      field:
        (searchState.sort?.field as ResourcesSortFields) ??
        ResourcesSortFields.Name,
      order: searchState.sort?.order ?? SortOrder.Asc,
    },
    input: {
      phrase: phraseToSend,
    },
    basic: true,
  });

  const resourceOptions = useMemo(() => {
    const resources = data ?? [];
    const resourceData = resources.filter(isNotNil).map((res) => {
      if (alreadyAddedResourceIds?.has(res.id)) {
        return {
          id: res.id,
          name: res.name,
          isDuplicate: true,
          icon: duplicateIconConfig.icon,
          iconTooltip: "Resource is already added to this action",
          color: duplicateIconConfig.color,
        };
      }
      return { id: res.id, name: res.name };
    });
    return formatSidebarOptions(resourceData);
  }, [data, alreadyAddedResourceIds]);

  const handleClose = () => {
    updateSidebarState({ activeSidebar: undefined });
  };

  const handleCancel = () => {
    setModalOpen(false);
    updateSidebarState({ activeSidebar: SidebarType.RESOURCE });
  };

  const handleSubmit = () => {
    const updatedResourcesIds = {
      resourceIds: sidebarState.selectedResourceIds,
    };

    const updatedEntities = updateEntities(stepState, updatedResourcesIds);

    updateStepState({
      entities: updatedEntities,
      selectedEntity: {
        ...stepState.selectedEntity,
        ...updatedResourcesIds,
      },
    });

    setPlanData((prevPlanData) => ({
      ...prevPlanData,
      [PlanField.PLAN_SETTINGS]: {
        planEntities: updatedEntities,
      },
    }));

    handleClose();
    updateSidebarState({ selectedResourceIds: [] });
    refetch();
  };

  const handleItemSelect = (selectedItem: string) => {
    updateSidebarState({
      selectedResourceIds: getSelectedOptions(
        sidebarState.selectedResourceIds ?? [],
        selectedItem
      ),
    });
  };

  const [modalOpen, setModalOpen] = useState(false);

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleApplyNewResourceGroup = (
    newResourceGroup: CreateResourceGroup
  ) => {
    const newResourceGroupWithIds = {
      ...newResourceGroup,
      resourceIds: sidebarState.selectedResourceIds,
    };

    const updatedResourceGroups = [
      ...(stepState.selectedEntity?.createResourceGroups ?? []),
      newResourceGroupWithIds,
    ];

    const updatedEntities = updateEntities(stepState, {
      createResourceGroups: updatedResourceGroups,
    });

    updateStepState({
      entities: updatedEntities,
      selectedEntity: {
        ...stepState.selectedEntity,
        createResourceGroups: updatedResourceGroups,
      },
    });

    const planDataAlreadyCreatedGroups =
      planData?.plan_settings?.createResourceGroups ?? [];

    const planDataUpdatedGroups = [
      ...planDataAlreadyCreatedGroups,
      newResourceGroupWithIds,
    ];

    setPlanData((prevData) => {
      return {
        ...prevData,
        [PlanField.PLAN_SETTINGS]: {
          ...prevData[PlanField.PLAN_SETTINGS],
          planEntities: updatedEntities,
          createResourceGroups: planDataUpdatedGroups,
        },
      };
    });

    handleClose();
    refetch();
    setModalOpen(false);
  };

  return (
    <>
      <Unless condition={modalOpen}>
        <SidebarSelector
          name="resource"
          title="Add Resources"
          onClose={handleClose}
          showValueInOptions
          onSubmit={handleSubmit}
          options={isSearchAllowed ? resourceOptions : []}
          onItemSelect={handleItemSelect}
          loading={loading}
          selectedOptions={sidebarState.selectedResourceIds ?? []}
          serverSideSearch
          noDataContent={
            !isSearchAllowed ? (
              <NoData message={getNoSearchResultsMessage()} />
            ) : undefined
          }
          onSearchChange={(val: string) => {
            updateSearchState({
              page: 1,
              filter: { ...searchState.filter, phrase: val },
            });
          }}
          renderPagination={
            <Pagination
              count={count}
              page={searchState.page}
              limit={searchState.limit}
              onPageChange={(p) => updateSearchState({ page: p })}
              onLimitChange={(l) => updateSearchState({ limit: l, page: 1 })}
              small
            />
          }
          customButtonsConfig={[
            {
              label: "Apply and Save as New Group",
              onClick: handleOpenModal,
              order: 1,
            },
          ]}
          onClear={() => {
            updateSidebarState({ selectedResourceIds: [] });
          }}
          isOpen
        />
      </Unless>
      <CreateResourceGroupModal
        isOpen={modalOpen}
        onApply={handleApplyNewResourceGroup}
        onCancel={handleCancel}
      />
    </>
  );
}
